import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Film, Trash2, Play, Loader2, X, RefreshCw, Zap, Link2, Edit3, ClipboardList, CheckCircle2 } from 'lucide-react'
import { useHighlightsApi } from './useHighlightsApi'
import ReelEditor from './ReelEditor'

function fmtTime(ms) {
  const sec = Math.floor((ms || 0) / 1000)
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = sec % 60
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}
function fmtAgo(iso) {
  if (!iso) return ''
  const d = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z')
  const diff = (Date.now() - d.getTime()) / 1000
  if (diff < 60) return `${Math.round(diff)}s ago`
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`
  return `${Math.round(diff / 86400)}d ago`
}

export default function HighlightReelBuilder({ reelCart }) {
  const api = useHighlightsApi()
  const [title, setTitle] = useState('')
  // Default ±10s padding (Step 25g). Applies to KW/Mux/chapter/cue segments.
  // LLM-detector moments use their bare span (detector='llm' branch below)
  // since the LLM already chose the right lead-up + post-goal tail. Editor
  // fine-tunes any segment in the Reel Editor via the ±10s In/Out buttons.
  const [preMs, setPreMs] = useState(10000)
  const [postMs, setPostMs] = useState(10000)
  // Per-reel cap on the moment-body length (BEFORE pre/post padding is
  // added). 0 means "use the full moment span as-detected" — current
  // behaviour. Otherwise the moment's end_ms is clamped to start_ms +
  // maxMomentMs, then padding is applied on top. Useful for tightening
  // long-span LLM moments (15-60s spans) down to a consistent shape.
  // Stored in ms. Default 0 = full span.
  const [maxMomentMs, setMaxMomentMs] = useState(0)
  const [creating, setCreating] = useState(false)
  const [createErr, setCreateErr] = useState(null)
  const [reels, setReels] = useState([])
  const [loadingList, setLoadingList] = useState(false)
  const [listErr, setListErr] = useState(null)
  const [playing, setPlaying] = useState(null) // { reel_id }
  const [editing, setEditing] = useState(null) // { reel_id }
  const [publishing, setPublishing] = useState(null) // { reel_id, reelRow }

  const loadReels = useCallback(async () => {
    setLoadingList(true)
    setListErr(null)
    try {
      const r = await api.listReels()
      setReels(r.reels || [])
    } catch (e) {
      setListErr(e.message)
    } finally {
      setLoadingList(false)
    }
  }, [api])

  useEffect(() => { loadReels() }, [])

  // Smart default title synthesized from the cart's _origin metadata so the
  // reel carries forward the selection criteria the editor used. The editor
  // can still type over it before clicking Build.
  const suggestedTitle = useMemo(() => makeDefaultTitle(reelCart?.cart), [reelCart?.cart])

  async function createReel() {
    if (!reelCart?.cart || reelCart.cart.length === 0) return
    setCreating(true)
    setCreateErr(null)
    try {
      // If the user didn't type a title, fall back to the auto-suggested one
      // so the reel is never anonymous.
      const finalTitle = title.trim() || suggestedTitle || undefined
      // Step 22 + max-span cap: when a cart entry carries the moment's
      // end_ms (KMs from KeyMomentsExplorer + KM matches from
      // TranscriptSearch), compute an explicit per-segment window so the
      // moment's full span is preserved instead of being truncated to a
      // fixed ±10s around start_ms. If maxMomentMs > 0, also clamp the
      // moment BODY (pre-padding) so long LLM-detected spans get
      // tightened uniformly. The backend reels.js reads window_start_ms /
      // window_end_ms when present (no schema change needed).
      const projectedSegments = reelCart.cart.map(s => {
        if (s.end_ms == null) return s   // raw cue match — leave alone
        const effectiveEnd = maxMomentMs > 0
          ? Math.min(s.end_ms, s.start_ms + maxMomentMs)
          : s.end_ms
        // Step 25g: LLM-detector moments already include lead-up + post-goal
        // commentary in their span — skip the ±pre/post padding for them.
        // KW/Mux/chapter/other detectors get the standard ±preMs/postMs.
        const isLlm = s.detector === 'llm'
        const segPre = isLlm ? 0 : preMs
        const segPost = isLlm ? 0 : postMs
        return {
          ...s,
          window_start_ms: Math.max(0, s.start_ms - segPre),
          window_end_ms: effectiveEnd + segPost,
        }
      })
      const r = await api.createReel({
        title: finalTitle,
        segments: projectedSegments,
        pre_ms: preMs,
        post_ms: postMs,
        // mode kept for back-compat on the worker side; both modes now
        // produce the same stitched HLS playback URL.
        mode: 'preview',
        // Capture how the cart was built so the editor can restore the
        // original candidate pool later (re-run the original search).
        origin: makeOriginPayload(reelCart.cart),
      })
      reelCart.clear?.()
      setTitle('')
      await loadReels()
      if (r?.reel_id) setPlaying({ reel_id: r.reel_id })
    } catch (e) {
      setCreateErr(e.message)
    } finally {
      setCreating(false)
    }
  }

  async function deleteReel(reel_id) {
    if (!confirm(`Delete reel ${reel_id}?\n\nThis removes the local record only. Encoded reels' Mux assets stay.`)) return
    try {
      await api.deleteReel(reel_id)
      loadReels()
    } catch (e) {
      alert(`Delete failed: ${e.message}`)
    }
  }

  const cartItems = reelCart?.cart || []

  return (
    <div>
      {/* Builder */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Film size={18} /> Build a reel
        </h3>

        {cartItems.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>
            Go to the <strong>Search</strong> tab and click the <strong>+</strong> button on any match to add it to the reel cart.
          </p>
        ) : (
          <>
            <div style={{ marginBottom: 12, fontSize: 13 }}>
              <strong>{cartItems.length}</strong> segment{cartItems.length === 1 ? '' : 's'} selected
              <button onClick={() => reelCart.clear()} title="Clear cart"
                style={{ marginLeft: 8, background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', fontSize: 12 }}>
                Clear all
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '70px 1fr 24px', columnGap: 8, rowGap: 4, fontSize: 12, lineHeight: 1.5, marginBottom: 12, maxHeight: 200, overflowY: 'auto', border: '1px solid #1f1f1f', padding: 8, borderRadius: 4 }}>
              {cartItems.map((s, i) => (
                <CartRow key={`${s.tbtv_id}-${s.start_ms}-${i}`} segment={s}
                  onRemove={() => reelCart.remove(s.tbtv_id, s.start_ms)} />
              ))}
            </div>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
              <input
                type="text"
                placeholder={suggestedTitle || 'Reel title (optional)'}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                title={suggestedTitle ? `Default if left blank: ${suggestedTitle}` : undefined}
                style={{ flex: 1, minWidth: 240, padding: '8px 10px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit' }}
              />
              <label style={{ fontSize: 12, color: '#888', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                pre (s):
                <input type="number" min={0} max={60} value={preMs / 1000}
                  onChange={(e) => setPreMs(Math.max(0, Math.min(60, parseInt(e.target.value, 10) || 0)) * 1000)}
                  style={{ width: 60, padding: '6px 8px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit' }} />
              </label>
              <label style={{ fontSize: 12, color: '#888', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                post (s):
                <input type="number" min={0} max={60} value={postMs / 1000}
                  onChange={(e) => setPostMs(Math.max(0, Math.min(60, parseInt(e.target.value, 10) || 0)) * 1000)}
                  style={{ width: 60, padding: '6px 8px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit' }} />
              </label>
              {/* Per-reel cap on moment body length (before pre/post). 0 =
                  use the detector's full moment span (current default).
                  Tightening here is the cheapest way to standardize clip
                  lengths without re-running the LLM detector. */}
              <label style={{ fontSize: 12, color: '#888', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                title="Cap the moment span (before pre/post padding) so long LLM moments get trimmed to a consistent shape. Full = use the detector's moment span as-is.">
                clip body:
                <select value={maxMomentMs}
                  onChange={(e) => setMaxMomentMs(parseInt(e.target.value, 10))}
                  style={{ padding: '6px 8px', background: '#111', border: '1px solid #333', borderRadius: 4, color: 'inherit', fontSize: 12 }}>
                  <option value={0}>Full moment span</option>
                  <option value={5000}>Tight (5s ≈ 1 cue)</option>
                  <option value={10000}>Short (10s ≈ 2 cues)</option>
                  <option value={15000}>Medium (15s ≈ 3 cues)</option>
                  <option value={20000}>Long (20s ≈ 4 cues)</option>
                  <option value={25000}>Loose (25s ≈ 5 cues)</option>
                </select>
              </label>
              <button onClick={createReel} disabled={creating || cartItems.length === 0}
                style={{ padding: '8px 16px', background: '#3b82f6', border: 'none', borderRadius: 4, color: 'white', cursor: 'pointer', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {creating ? <Loader2 size={14} className="spin" /> : <Zap size={14} />}
                Build reel
              </button>
            </div>

            {createErr && <div style={{ color: 'crimson', fontSize: 13 }}>{createErr}</div>}
          </>
        )}
      </div>

      {/* Reel list */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h3 style={{ margin: 0 }}>Past reels</h3>
          <button onClick={loadReels} disabled={loadingList} title="Refresh"
            style={{ background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', padding: 4 }}>
            <RefreshCw size={14} className={loadingList ? 'spin' : ''} />
          </button>
        </div>

        {listErr && <div style={{ color: 'crimson' }}>{listErr}</div>}
        {!listErr && reels.length === 0 && !loadingList && (
          <p className="muted" style={{ margin: 0 }}>No reels yet. Build one above.</p>
        )}

        {reels.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #2a2a2a' }}>
                <th style={{ padding: '8px 6px', textAlign: 'left' }}>Title</th>
                <th style={{ padding: '8px 6px', textAlign: 'left', width: 140 }}>Status</th>
                <th style={{ padding: '8px 6px', textAlign: 'left', width: 120 }}>Created</th>
                <th style={{ padding: '8px 6px', textAlign: 'right', width: 200 }}></th>
              </tr>
            </thead>
            <tbody>
              {reels.map(r => {
                // All reels are playable via the stitched HLS URL now,
                // regardless of legacy mode/status from previous attempts.
                const playbackUrl = r.playback_url || `https://watch.tenband.tv/api/highlights/reels/${r.reel_id}/stream.m3u8`
                return (
                  <tr key={r.reel_id} style={{ borderBottom: '1px solid #1a1a1a' }}>
                    <td style={{ padding: '8px 6px' }}>
                      <div style={{ fontWeight: 500 }}>{r.title || '(untitled)'}</div>
                      <div style={{ fontSize: 11, color: '#777', fontFamily: 'monospace' }}>
                        {r.reel_id}
                        {r.tbtv_id && ` · ${r.tbtv_id}`}
                      </div>
                    </td>
                    <td style={{ padding: '8px 6px', color: '#22c55e', fontSize: 13 }}>
                      🟢 Ready
                    </td>
                    <td style={{ padding: '8px 6px', color: '#888', fontSize: 12 }}>
                      {fmtAgo(r.created_at)}
                    </td>
                    <td style={{ padding: '8px 6px', textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: 6 }}>
                        <button
                          onClick={() => { navigator.clipboard?.writeText(playbackUrl) }}
                          title="Copy share URL"
                          style={{ padding: '4px 8px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Link2 size={11} /> Copy URL
                        </button>
                        <button onClick={() => setEditing({ reel_id: r.reel_id })}
                          title="Edit reel"
                          style={{ padding: '4px 8px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Edit3 size={11} /> Edit
                        </button>
                        {r.tbtv_id ? (
                          <span title={`Published to catalog as ${r.tbtv_id}`}
                            style={{ padding: '4px 8px', background: 'transparent', border: '1px solid #166534', borderRadius: 4, color: '#22c55e', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <CheckCircle2 size={11} /> Published
                          </span>
                        ) : (
                          <button onClick={() => setPublishing({ reel_id: r.reel_id, reelRow: r })}
                            title="Publish to content catalog as a Highlights item"
                            style={{ padding: '4px 8px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: '#facc15', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <ClipboardList size={11} /> Publish
                          </button>
                        )}
                        <button onClick={() => setPlaying({ reel_id: r.reel_id, playback_url: playbackUrl })}
                          style={{ padding: '4px 10px', background: '#3b82f6', border: 'none', borderRadius: 4, color: 'white', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Play size={11} /> Play
                        </button>
                        <button onClick={() => deleteReel(r.reel_id)} title="Delete reel"
                          style={{ padding: '4px 8px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: '#dc2626', cursor: 'pointer', fontSize: 12 }}>
                          <Trash2 size={11} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {playing && (
        <ReelPlayer
          reelId={playing.reel_id}
          playbackUrl={playing.playback_url}
          onClose={() => setPlaying(null)}
        />
      )}

      {editing && (
        <ReelEditor
          reelId={editing.reel_id}
          onClose={() => { setEditing(null); loadReels() }}
        />
      )}

      {publishing && (
        <PublishToCatalogDialog
          reelId={publishing.reel_id}
          reelRow={publishing.reelRow}
          onClose={() => setPublishing(null)}
          onPublished={() => { setPublishing(null); loadReels() }}
        />
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } } .spin { animation: spin 1s linear infinite; }`}</style>
    </div>
  )
}

// Step 32: Publish-to-Catalog dialog. Loads the reel's publish-info
// (dominant source summaries + duration + suggested release_date), lets
// the editor tune the title / descriptions / tags / status, and POSTs to
// /api/vod/create-from-reel. On success the reel row gets a green
// "Published" badge and the new tbtv_id is linked.
function PublishToCatalogDialog({ reelId, reelRow, onClose, onPublished }) {
  const api = useHighlightsApi()
  const [loading, setLoading] = useState(true)
  const [info, setInfo] = useState(null)
  const [loadErr, setLoadErr] = useState(null)
  const [title, setTitle] = useState('')
  const [shortDesc, setShortDesc] = useState('')
  const [longDesc, setLongDesc] = useState('')
  const [tagsInput, setTagsInput] = useState('')
  const [releaseDate, setReleaseDate] = useState('')
  const [status, setStatus] = useState('draft')
  const [submitting, setSubmitting] = useState(false)
  const [submitErr, setSubmitErr] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setLoadErr(null)
    api.reelPublishInfo(reelId)
      .then(pi => {
        if (cancelled) return
        setInfo(pi)
        setTitle(`HIGHLIGHTS: ${pi.title || ''}`.slice(0, 240))
        setShortDesc(pi.short_description || '')
        setLongDesc(pi.long_description || '')
        setTagsInput('tbtv-highlights')
        setReleaseDate(pi.release_date || '')
      })
      .catch(e => !cancelled && setLoadErr(e.message))
      .finally(() => !cancelled && setLoading(false))
    return () => { cancelled = true }
  }, [reelId, api])

  async function submit() {
    setSubmitting(true)
    setSubmitErr(null)
    try {
      const tags = tagsInput.split(',').map(t => t.trim()).filter(Boolean)
      const r = await api.publishReelToCatalog({
        reel_id: reelId,
        title_override: title,
        short_description_override: shortDesc,
        long_description_override: longDesc,
        tags,
        release_date: releaseDate || null,
        status,
      })
      onPublished(r)
    } catch (e) {
      setSubmitErr(e.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#0d0d0d', border: '1px solid #2a2a2a', borderRadius: 8, padding: 20, width: 560, maxHeight: '90vh', overflow: 'auto', color: '#ddd' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>
            <ClipboardList size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            Publish to Catalog
          </h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#888', cursor: 'pointer' }}><X size={16} /></button>
        </div>

        {loading && <div style={{ color: '#888', fontSize: 13 }}><Loader2 size={14} className="spin" style={{ verticalAlign: 'middle' }} /> Loading reel info…</div>}
        {loadErr && <div style={{ color: '#dc2626', fontSize: 13, padding: 10, background: '#7f1d1d', borderRadius: 4 }}>Failed to load: {loadErr}</div>}

        {info && (
          <>
            <div style={{ fontSize: 11, color: '#777', marginBottom: 12, fontFamily: 'monospace' }}>
              {reelId} · {info.duration_seconds}s · {info.source_tbtv_ids?.length || 0} source video(s)
              {info.dominant_source_tbtv_id && ` · dominant: ${info.dominant_source_tbtv_id}`}
            </div>

            <Field label="Title">
              <input value={title} onChange={e => setTitle(e.target.value)}
                style={inputStyle} />
            </Field>

            <Field label="Short description">
              <textarea value={shortDesc} onChange={e => setShortDesc(e.target.value)}
                maxLength={200} rows={2} style={{ ...inputStyle, fontFamily: 'inherit' }} />
              <div style={{ fontSize: 11, color: '#555', textAlign: 'right' }}>{shortDesc.length}/200 · from TBTV-AI summary of dominant source</div>
            </Field>

            <Field label="Long description">
              <textarea value={longDesc} onChange={e => setLongDesc(e.target.value)}
                rows={4} style={{ ...inputStyle, fontFamily: 'inherit' }} />
              <div style={{ fontSize: 11, color: '#555', textAlign: 'right' }}>from Mux summary of dominant source</div>
            </Field>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Release date">
                <input type="date" value={releaseDate || ''} onChange={e => setReleaseDate(e.target.value)}
                  style={inputStyle} />
              </Field>
              <Field label="Status">
                <select value={status} onChange={e => setStatus(e.target.value)} style={inputStyle}>
                  <option value="draft">Draft</option>
                  <option value="active">Active (publish to watch)</option>
                </select>
              </Field>
            </div>

            <Field label="Tags (comma-separated; tbtv-highlights always added)">
              <input value={tagsInput} onChange={e => setTagsInput(e.target.value)}
                placeholder="tbtv-highlights, pihl, championship"
                style={inputStyle} />
            </Field>

            {submitErr && <div style={{ color: '#dc2626', fontSize: 13, padding: 10, background: '#7f1d1d', borderRadius: 4, marginTop: 8 }}>Publish failed: {submitErr}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button onClick={onClose} disabled={submitting}
                style={{ padding: '8px 14px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: '#ddd', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={submit} disabled={submitting || !title.trim()}
                style={{ padding: '8px 14px', background: '#3b82f6', border: 'none', borderRadius: 4, color: 'white', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {submitting ? <Loader2 size={14} className="spin" /> : <ClipboardList size={14} />}
                Publish to Catalog
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

const inputStyle = {
  width: '100%', padding: '8px 10px', background: '#1a1a1a',
  border: '1px solid #333', borderRadius: 4, color: '#ddd', fontSize: 13,
  boxSizing: 'border-box',
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 12, color: '#888', marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  )
}


function CartRow({ segment, onRemove }) {
  return (
    <>
      <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#888' }}>
        {fmtTime(segment.start_ms)}
      </div>
      <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#aaa', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {segment.tbtv_id}
      </div>
      <button onClick={onRemove} title="Remove"
        style={{ background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', padding: 0 }}>
        <X size={14} />
      </button>
    </>
  )
}

// Unified player: fetches the reel by id, plays encoded or preview as appropriate.
// Plays a reel via the stitched HLS URL served by hls-stitcher. All reels
// — single-source, multi-source, any age — play through one <mux-player>
// pointed at /api/highlights/reels/<reel_id>/stream.m3u8. The player handles
// EXT-X-DISCONTINUITY between source clips natively per HLS spec.
function ReelPlayer({ reelId, playbackUrl, onClose }) {
  const api = useHighlightsApi()
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)

  useEffect(() => {
    let cancelled = false
    api.getReel(reelId)
      .then(r => { if (!cancelled) setData(r) })
      .catch(e => { if (!cancelled) setErr(e.message) })
    return () => { cancelled = true }
  }, [reelId])

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const src = playbackUrl || data?.playback_url
  const segmentCount = data?.segments?.length

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.() }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
    >
      <div style={{ background: '#111', border: '1px solid #2a2a2a', borderRadius: 8, width: 'min(960px, 96vw)', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid #2a2a2a', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{data?.title || reelId}</div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 2, fontFamily: 'monospace' }}>
              {segmentCount ? `${segmentCount} segment${segmentCount === 1 ? '' : 's'} · stitched HLS` : 'stitched HLS'}
            </div>
          </div>
          <div style={{ display: 'inline-flex', gap: 6 }}>
            {src && (
              <button onClick={() => navigator.clipboard?.writeText(src)} title="Copy share URL"
                style={{ background: 'transparent', border: '1px solid #333', borderRadius: 4, color: '#888', cursor: 'pointer', padding: '4px 8px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Link2 size={12} /> Copy URL
              </button>
            )}
            <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', padding: 4 }}>
              <X size={18} />
            </button>
          </div>
        </div>

        <div style={{ background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 360 }}>
          {!src && !err && <div style={{ color: '#888', padding: 32 }}>Loading…</div>}
          {err && <div style={{ color: 'crimson', padding: 32 }}>{err}</div>}
          {src && (
            <mux-player
              key={src}
              src={src}
              stream-type="on-demand"
              accent-color="#3b82f6"
              autoplay="any"
              style={{ width: '100%', maxHeight: '70vh', aspectRatio: '16/9', display: 'block' }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

// Build a smart default reel title from cart metadata. Three flavors:
//   - All cart items came from one TranscriptSearch query → '"Query" — N clips from M videos'
//   - Multiple distinct queries → 'Search: "q1", "q2" — N clips'
//   - All from Key Moments tab with a filter → 'Top goals (12 clips)' / 'Mux + AI key moments (8 clips)'
//   - No metadata (legacy items / Caption Library single-video adds) → 'N clips from M videos'
// Returns null when the cart is empty.
function makeDefaultTitle(cart) {
  if (!Array.isArray(cart) || cart.length === 0) return null
  const N = cart.length
  const distinctVideos = new Set(cart.map(s => s.tbtv_id)).size
  const Mlabel = `${distinctVideos} video${distinctVideos === 1 ? '' : 's'}`
  const Nlabel = `${N} clip${N === 1 ? '' : 's'}`

  const origins = cart.map(s => s._origin).filter(Boolean)
  if (origins.length === 0) {
    return `${Nlabel} from ${Mlabel}`
  }

  const queries = [...new Set(origins.filter(o => o.kind === 'search' && o.query).map(o => o.query))]
  const kmOrigins = origins.filter(o => o.kind === 'key_moment')

  // Single search query → highlight it.
  if (queries.length === 1 && origins.every(o => o.kind === 'search')) {
    return distinctVideos > 1
      ? `"${queries[0]}" — ${Nlabel} from ${Mlabel}`
      : `"${queries[0]}" — ${Nlabel}`
  }

  // Multiple search queries.
  if (queries.length > 1 && origins.every(o => o.kind === 'search')) {
    const head = queries.slice(0, 3).map(q => `"${q}"`).join(', ')
    const rest = queries.length > 3 ? ` +${queries.length - 3} more` : ''
    return `Search: ${head}${rest} — ${Nlabel}`
  }

  // All from Key Moments tab.
  if (kmOrigins.length === origins.length) {
    const typeSets = new Set()
    for (const o of kmOrigins) {
      if (o.moment_type) typeSets.add(o.moment_type)
    }
    const lead = typeSets.size > 0 && typeSets.size <= 3
      ? `${[...typeSets].map(t => t.replace('_', ' ')).join(' + ')}`
      : 'Key moments'
    return distinctVideos > 1
      ? `${capitalize(lead)} — ${Nlabel} from ${Mlabel}`
      : `${capitalize(lead)} — ${Nlabel}`
  }

  // Mixed cart.
  return `Mixed highlights — ${Nlabel} from ${Mlabel}`
}

function capitalize(s) { return s ? s[0].toUpperCase() + s.slice(1) : s }

// Build the persisted origin payload from cart segments' _origin stamps.
// Persisted on highlight_reels.origin and read back by ReelEditor to
// re-fetch the same candidate pool the cart was built from.
function makeOriginPayload(cart) {
  if (!Array.isArray(cart) || cart.length === 0) return null
  const origins = cart.map(s => s._origin).filter(Boolean)
  if (origins.length === 0) return null

  const searches = origins.filter(o => o.kind === 'search')
  const keyMoments = origins.filter(o => o.kind === 'key_moment')

  // Pure search cart → record the distinct queries (and the mode used by
  // the first one; mode rarely varies within a single building session).
  if (searches.length === origins.length) {
    const queries = [...new Set(searches.map(o => o.query).filter(Boolean))]
    if (queries.length === 0) return null
    return { kind: 'search', queries, mode: searches[0].mode || null }
  }

  // Pure key-moments cart → record the filters that produced it. The pool
  // tab already shows all key moments per source video, so restoration
  // here is mostly metadata for downstream tooling.
  if (keyMoments.length === origins.length) {
    const types = [...new Set(keyMoments.flatMap(o => o.types_filter || []))]
    const sources = [...new Set(keyMoments.flatMap(o => o.sources_filter || []))]
    return {
      kind: 'key_moments',
      filters: {
        types: types.length ? types : null,
        sources: sources.length ? sources : null,
        min_score: keyMoments[0].min_score ?? null,
      },
    }
  }

  // Mixed cart — preserve both pieces so the editor can show whatever
  // applies. (Currently informational; layered restoration is future work.)
  const queries = [...new Set(searches.map(o => o.query).filter(Boolean))]
  return {
    kind: 'mixed',
    queries: queries.length ? queries : null,
  }
}
